/**
 * WISHLIST-STORE-1 — Store Access Settings (admin/staff only · MOCK) · Jun 2026.
 *
 * STRICT INVARIANTS (do not relax without explicit nominal GO):
 *   • Admin + staff only (RBAC enforced by AdminLayout sidebar + defensive
 *     guard inside the page).
 *   • Zero DB writes · zero API calls · 100% mock data inline.
 *   • Zero customer-facing surface · zero ConsentGate · zero notifications ·
 *     zero billing runtime · zero marketplace activation.
 *
 * Opens Phase 2 of GEAR-WISHLIST safely: first admin-only screen for the
 * Store Access service configuration cascade (country / sport / vertical /
 * shop) + monetization + consent gate + offer capability + audit readiness.
 */
import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";
import { ShieldAlert, Lock, Store, Cog, Inbox, FileCheck } from "lucide-react";

// QA-I18N-ADMIN-ES-FULL-1 — bilingual copy for this admin-only mock page.
// Slug-style contract keys are preserved in English (status codes, capability
// names, audit rules) — they document the future backend contract names.
const COPY = {
  en: {
    pageTitle: "Store Access Settings (WISHLIST-STORE-1)",
    pageSubtitle: "Admin/Staff only · 100% mock · zero writes · zero customer-facing · zero real runtime.",
    bannerTitle: "WISHLIST-STORE-1 admin-only mock · Phase 2 GEAR opener",
    bannerBody1: "Mock screen to visualise and validate the future configuration of the ",
    bannerBodyStrong: "Wishlist Store Access",
    bannerBody2: " service. Inline data (no DB, no API). No ConsentGate, no real consent, no billing, no marketplace activation, no real notifications. Blocked for external and operational profiles.",
    s1: { title: "1. Service status catalogue (mock)", sub: "9 configurable service states" },
    s2: { title: "2. Scope configuration cascade (mock)", sub: "country · sport · vertical · service tier · access level" },
    s3: { title: "3. Shop eligibility (mock)", sub: (n) => `${n} mock shops across 4 countries` },
    s4: { title: "4. Data categories mock", sub: "What a shop could see in the future (NONE exposed today)" },
    s4Lock: "No category is exposed today. Blocked until ConsentGate + counsel per jurisdiction.",
    s5: { title: "5. User permission / consent gate states (mock)", sub: "7 future states · all gated by ConsentGate" },
    s6: { title: "6. Monetization modes (mock)", sub: "8 configurable future modes · zero billing runtime" },
    s7: { title: "7. Offer capability matrix (mock)", sub: "What a shop could do · most actions blocked" },
    s8: { title: "8. Audit readiness (mock · GEAR-AUDIT-1 alignment)", sub: "Every future shop access MUST be audited" },
    th_scopeId: "Scope ID", th_country: "Country", th_sport: "Sport", th_vertical: "Vertical",
    th_tier: "Tier", th_access: "Access", th_status: "Status",
    th_capability: "Capability", th_reason: "Reason",
    trialDaysLeft: (n) => `${n} trial days left`,
    consentNotes: {
      not_required_internal_mock: "Admin preview · no consent needed",
      pending_consentgate: "Awaiting ConsentGate deployment + counsel",
      granted_future: "User would grant via Settings (future)",
      revoked_future: "User can revoke any time (future)",
      expired_future: "Automatic expiry per jurisdiction",
      blocked_by_country_policy: "Country legal_zone blocks transfer",
      blocked_by_minor_policy: "Minor data protection (COPPA/GDPR-K)",
    },
    monetizationNotes: {
      free: "Free access for the shop",
      trial: "N-day trial then auto-disable",
      paid: "Monthly/annual subscription",
      invoice: "Manual invoicing B2B",
      subscription: "Recurring billing (future Stripe)",
      marketplace_partner_paid: "Partner pays on behalf of shop",
      country_specific_pricing: "Per-country price tier",
      sport_specific_pricing: "Per-sport price tier",
    },
    offerReasons: {
      view_wishlist: "blocked until consent + runtime",
      create_offer_opportunity: "GEAR-2/3 mock surfaces · is_mock=true",
      send_offer: "blocked · no real notification runtime",
      trigger_notification: "blocked · Resend MOCK MODE only",
      checkout: "blocked · marketplace activation not granted",
    },
    auditRules: [
      "Every shop access MUST emit `gear_access_audit_log` entry (rule 1 · GEAR-AUDIT-1).",
      "Event types catalogued: view_gear_profile · shop_view_wishlist · shop_create_offer_opportunity · permission_granted · permission_revoked · access_denied · etc. (45 total)",
      "NO silent shop access to L2+ data (rule 8).",
      "Zero raw PII — SHA-256 hashes only for IP/UA (rule 11).",
      "Append-only — UPDATE/DELETE prohibited without counsel sign-off (rule 14).",
      "Retention default 7 years · counsel per jurisdiction override (rule 12).",
    ],
    footerLegal: "Legal package accepted / counsel OK for authorized internal/admin-only technical work. Public activation remains blocked until explicit nominal GO.",
  },
  es: {
    pageTitle: "Configuración de Store Access (WISHLIST-STORE-1)",
    pageSubtitle: "Sólo admin/staff · 100% mock · cero escrituras · cero customer-facing · cero runtime real.",
    bannerTitle: "WISHLIST-STORE-1 mock sólo admin · apertura Fase 2 GEAR",
    bannerBody1: "Pantalla mock para visualizar y validar la futura configuración del servicio ",
    bannerBodyStrong: "Wishlist Store Access",
    bannerBody2: ". Datos inline (sin DB, sin API). Sin ConsentGate, sin consentimiento real, sin billing, sin activación de marketplace, sin notificaciones reales. Bloqueada para perfiles externos y operativos.",
    s1: { title: "1. Catálogo de estados del servicio (mock)", sub: "9 estados configurables del servicio" },
    s2: { title: "2. Cascada de configuración de scope (mock)", sub: "país · deporte · vertical · tier de servicio · nivel de acceso" },
    s3: { title: "3. Elegibilidad de tienda (mock)", sub: (n) => `${n} tiendas mock en 4 países` },
    s4: { title: "4. Categorías de datos (mock)", sub: "Lo que una tienda PODRÍA ver en el futuro (NINGUNA expuesta hoy)" },
    s4Lock: "Ninguna categoría está expuesta hoy. Bloqueado hasta ConsentGate + counsel por jurisdicción.",
    s5: { title: "5. Estados de permiso de usuario / consent gate (mock)", sub: "7 estados futuros · todos gestionados por ConsentGate" },
    s6: { title: "6. Modos de monetización (mock)", sub: "8 modos futuros configurables · cero runtime de billing" },
    s7: { title: "7. Matriz de capacidades de oferta (mock)", sub: "Lo que una tienda podría hacer · la mayoría de acciones bloqueadas" },
    s8: { title: "8. Preparación de auditoría (mock · alineación GEAR-AUDIT-1)", sub: "Todo acceso futuro de tienda DEBE ser auditado" },
    th_scopeId: "ID de scope", th_country: "País", th_sport: "Deporte", th_vertical: "Vertical",
    th_tier: "Tier", th_access: "Acceso", th_status: "Estado",
    th_capability: "Capacidad", th_reason: "Motivo",
    trialDaysLeft: (n) => `${n} días de prueba restantes`,
    consentNotes: {
      not_required_internal_mock: "Vista admin · no se requiere consentimiento",
      pending_consentgate: "Esperando despliegue de ConsentGate + counsel",
      granted_future: "El usuario lo otorgaría desde Ajustes (futuro)",
      revoked_future: "El usuario puede revocar en cualquier momento (futuro)",
      expired_future: "Expiración automática por jurisdicción",
      blocked_by_country_policy: "El legal_zone del país bloquea la transferencia",
      blocked_by_minor_policy: "Protección de datos de menores (COPPA/GDPR-K)",
    },
    monetizationNotes: {
      free: "Acceso gratuito para la tienda",
      trial: "Prueba de N días y luego auto-desactivación",
      paid: "Suscripción mensual/anual",
      invoice: "Facturación manual B2B",
      subscription: "Cobro recurrente (futuro Stripe)",
      marketplace_partner_paid: "Un partner paga en nombre de la tienda",
      country_specific_pricing: "Tier de precio por país",
      sport_specific_pricing: "Tier de precio por deporte",
    },
    offerReasons: {
      view_wishlist: "bloqueado hasta consent + runtime",
      create_offer_opportunity: "Superficies mock GEAR-2/3 · is_mock=true",
      send_offer: "bloqueado · sin runtime real de notificación",
      trigger_notification: "bloqueado · Resend sólo en MOCK MODE",
      checkout: "bloqueado · activación de marketplace no concedida",
    },
    auditRules: [
      "Todo acceso de tienda DEBE emitir una entrada en `gear_access_audit_log` (regla 1 · GEAR-AUDIT-1).",
      "Tipos de evento catalogados: view_gear_profile · shop_view_wishlist · shop_create_offer_opportunity · permission_granted · permission_revoked · access_denied · etc. (45 en total)",
      "SIN acceso silencioso de la tienda a datos L2+ (regla 8).",
      "Cero PII raw — sólo hashes SHA-256 para IP/UA (regla 11).",
      "Append-only — UPDATE/DELETE prohibido sin sign-off de counsel (regla 14).",
      "Retención por defecto 7 años · override de counsel por jurisdicción (regla 12).",
    ],
    footerLegal: "Legal package accepted / counsel OK for authorized internal/admin-only technical work. Public activation remains blocked until explicit nominal GO.",
  },
};

const SERVICE_STATUSES = [
  { key: "disabled", tone: "rose", label: "disabled" },
  { key: "open_free", tone: "emerald", label: "open_free" },
  { key: "trial", tone: "amber", label: "trial" },
  { key: "paid", tone: "indigo", label: "paid" },
  { key: "paid_by_shop", tone: "indigo", label: "paid_by_shop" },
  { key: "paid_by_marketplace_partner", tone: "indigo", label: "paid_by_marketplace_partner" },
  { key: "country_specific", tone: "slate", label: "country_specific" },
  { key: "sport_specific", tone: "slate", label: "sport_specific" },
  { key: "beta_internal_only", tone: "slate", label: "beta_internal_only" },
];

const SCOPE_ROWS = [
  { id: "SC-ES-TENN-RL", country: "ES", sport: "tennis", vertical: "racket_lab", tier: "L2", access_level: "scoped", service_status: "trial" },
  { id: "SC-IT-TENN-STORE", country: "IT", sport: "tennis", vertical: "store_access", tier: "L0", access_level: "aggregated", service_status: "disabled" },
  { id: "SC-US-TENN-WL", country: "US", sport: "tennis", vertical: "wishlist_offers", tier: "L1", access_level: "pseudonymised", service_status: "beta_internal_only" },
  { id: "SC-ES-PADL-RL", country: "ES", sport: "padel", vertical: "racket_lab", tier: "L2", access_level: "scoped", service_status: "open_free" },
  { id: "SC-FR-TENN-STORE", country: "FR", sport: "tennis", vertical: "store_access", tier: "L1", access_level: "pseudonymised", service_status: "beta_internal_only" },
];

const SHOP_ELIGIBILITY = [
  { id: "SHOP-MOCK-ES-MADRID", name: "Demo Padel & Tennis Madrid", state: "trial_active", trial_days_left: 14, country: "ES", sport: "tennis" },
  { id: "SHOP-MOCK-ES-BARCELONA", name: "Barcelona Stringing Co.", state: "pending_approval", country: "ES", sport: "tennis" },
  { id: "SHOP-MOCK-IT-MILAN", name: "Milano Racket Studio", state: "blocked", country: "IT", sport: "tennis" },
  { id: "SHOP-MOCK-US-NYC", name: "NYC Internal Beta Lab", state: "internal_beta", country: "US", sport: "tennis" },
  { id: "SHOP-MOCK-ES-VALENCIA", name: "Valencia Padel Hub", state: "paid_active", country: "ES", sport: "padel" },
  { id: "SHOP-MOCK-FR-LYON", name: "Lyon Tennis Atelier", state: "expired", country: "FR", sport: "tennis" },
];

const DATA_CATEGORIES = [
  "gear_profile", "rackets", "string_setups", "tensions",
  "material_wishlist", "technical_history", "offer_opportunities",
];

const CONSENT_GATE_STATES = [
  { key: "not_required_internal_mock", tone: "slate", note: "Admin preview · no consent needed" },
  { key: "pending_consentgate", tone: "amber", note: "Awaiting ConsentGate deployment + counsel" },
  { key: "granted_future", tone: "emerald", note: "User would grant via Settings (future)" },
  { key: "revoked_future", tone: "rose", note: "User can revoke any time (future)" },
  { key: "expired_future", tone: "slate", note: "Automatic expiry per jurisdiction" },
  { key: "blocked_by_country_policy", tone: "rose", note: "Country legal_zone blocks transfer" },
  { key: "blocked_by_minor_policy", tone: "rose", note: "Minor data protection (COPPA/GDPR-K)" },
];

const MONETIZATION_MODES = [
  { key: "free", note: "Free access for the shop" },
  { key: "trial", note: "N-day trial then auto-disable" },
  { key: "paid", note: "Monthly/annual subscription" },
  { key: "invoice", note: "Manual invoicing B2B" },
  { key: "subscription", note: "Recurring billing (future Stripe)" },
  { key: "marketplace_partner_paid", note: "Partner pays on behalf of shop" },
  { key: "country_specific_pricing", note: "Per-country price tier" },
  { key: "sport_specific_pricing", note: "Per-sport price tier" },
];

const OFFER_CAPABILITY = [
  { capability: "view_wishlist", status: "blocked", reason: "blocked until consent + runtime" },
  { capability: "create_offer_opportunity", status: "mock_only", reason: "GEAR-2/3 mock surfaces · is_mock=true" },
  { capability: "send_offer", status: "blocked", reason: "blocked · no real notification runtime" },
  { capability: "trigger_notification", status: "blocked", reason: "blocked · Resend MOCK MODE only" },
  { capability: "checkout", status: "blocked", reason: "blocked · marketplace activation not granted" },
];

const AUDIT_READINESS = [
  "Every shop access MUST emit `gear_access_audit_log` entry (rule 1 · GEAR-AUDIT-1).",
  "Event types catalogued: view_gear_profile · shop_view_wishlist · shop_create_offer_opportunity · permission_granted · permission_revoked · access_denied · etc. (45 total)",
  "NO silent shop access to L2+ data (rule 8).",
  "Cero raw PII — SHA-256 hashes only for IP/UA (rule 11).",
  "Append-only — UPDATE/DELETE prohibited without counsel sign-off (rule 14).",
  "Retention default 7 years · counsel per jurisdiction override (rule 12).",
];

const Pill = ({ tone = "slate", testId, children }) => {
  const tones = {
    slate: "bg-slate-100 text-slate-700",
    emerald: "bg-emerald-100 text-emerald-800",
    amber: "bg-amber-100 text-amber-800",
    rose: "bg-rose-100 text-rose-800",
    indigo: "bg-indigo-100 text-indigo-800",
  };
  return (
    <span data-testid={testId} className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
};

const Section = ({ icon: Icon, title, subtitle, testId, children }) => (
  <section data-testid={testId} className="mb-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
    <header className="mb-4 flex items-center gap-3 border-b border-slate-100 pb-3">
      {Icon ? <Icon className="h-5 w-5 text-emerald-700" /> : null}
      <div>
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        {subtitle ? <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p> : null}
      </div>
    </header>
    {children}
  </section>
);

const AdminGearStoreAccess = () => {
  const { user } = useAuth();
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;
  const role = user?.role;
  if (role !== "admin" && role !== "staff") {
    return <Navigate to="/admin" replace />;
  }

  return (
    <div data-testid="admin-gear-store-access" className="mx-auto max-w-5xl px-4 py-6">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold text-slate-900">{c.pageTitle}</h1>
        <p className="mt-1 text-xs text-slate-500">{c.pageSubtitle}</p>
      </header>

      <div data-testid="wls1-banner" className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4">
        <div className="flex items-start gap-3">
          <ShieldAlert className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-700" />
          <div className="text-xs text-amber-900">
            <p className="font-semibold">{c.bannerTitle}</p>
            <p className="mt-1 leading-5">
              {c.bannerBody1}<strong>{c.bannerBodyStrong}</strong>{c.bannerBody2}
            </p>
          </div>
        </div>
      </div>

      <Section icon={Store} title={c.s1.title} subtitle={c.s1.sub} testId="wls1-service-statuses">
        <ul className="flex flex-wrap gap-2">
          {SERVICE_STATUSES.map((s) => (
            <li key={s.key} data-testid={`wls1-service-status-${s.key}`}>
              <Pill tone={s.tone}>{s.label}</Pill>
            </li>
          ))}
        </ul>
      </Section>

      <Section icon={Cog} title={c.s2.title} subtitle={c.s2.sub} testId="wls1-scope-table">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="border-b border-slate-200 text-left text-slate-500">
              <tr><th className="py-1 pr-2">{c.th_scopeId}</th><th>{c.th_country}</th><th>{c.th_sport}</th><th>{c.th_vertical}</th><th>{c.th_tier}</th><th>{c.th_access}</th><th>{c.th_status}</th></tr>
            </thead>
            <tbody>
              {SCOPE_ROWS.map((r) => (
                <tr key={r.id} data-testid={`wls1-scope-row-${r.id}`} className="border-b border-slate-100">
                  <td className="py-1 pr-2 font-mono text-[10px]">{r.id}</td>
                  <td className="font-mono">{r.country}</td>
                  <td>{r.sport}</td>
                  <td>{r.vertical}</td>
                  <td><Pill tone="indigo">{r.tier}</Pill></td>
                  <td>{r.access_level}</td>
                  <td><Pill tone={r.service_status === "disabled" ? "rose" : r.service_status === "trial" ? "amber" : r.service_status === "beta_internal_only" ? "slate" : "emerald"}>{r.service_status}</Pill></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section icon={Store} title={c.s3.title} subtitle={c.s3.sub(SHOP_ELIGIBILITY.length)} testId="wls1-shop-eligibility">
        <ul className="grid gap-2 sm:grid-cols-2">
          {SHOP_ELIGIBILITY.map((s) => {
            const tone = s.state === "blocked" || s.state === "expired" ? "rose" : s.state === "trial_active" ? "amber" : s.state === "paid_active" ? "emerald" : s.state === "internal_beta" ? "slate" : "indigo";
            return (
              <li key={s.id} data-testid={`wls1-shop-${s.id}`} className="rounded border border-slate-200 bg-white p-2 text-xs">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] text-slate-500">{s.id}</span>
                  <Pill tone={tone}>{s.state}</Pill>
                </div>
                <p className="mt-1 font-semibold text-slate-900">{s.name}</p>
                <p className="text-[10px] text-slate-500">{s.country} · {s.sport}{s.trial_days_left ? ` · ${c.trialDaysLeft(s.trial_days_left)}` : ""}</p>
              </li>
            );
          })}
        </ul>
      </Section>

      <Section icon={FileCheck} title={c.s4.title} subtitle={c.s4.sub} testId="wls1-data-categories">
        <ul className="flex flex-wrap gap-2">
          {DATA_CATEGORIES.map((cat) => (
            <li key={cat} data-testid={`wls1-category-${cat}`}><Pill tone="indigo">{cat}</Pill></li>
          ))}
        </ul>
        <p className="mt-2 flex items-center gap-2 rounded bg-rose-50 px-2 py-1 text-[11px] text-rose-800">
          <Lock className="h-3 w-3" />
          <span>{c.s4Lock}</span>
        </p>
      </Section>

      <Section icon={ShieldAlert} title={c.s5.title} subtitle={c.s5.sub} testId="wls1-consent-states">
        <ul className="space-y-1">
          {CONSENT_GATE_STATES.map((cs) => (
            <li key={cs.key} data-testid={`wls1-consent-state-${cs.key}`} className="flex items-center justify-between rounded border border-slate-200 bg-white p-2 text-xs">
              <Pill tone={cs.tone}>{cs.key}</Pill>
              <span className="text-[11px] text-slate-600">{c.consentNotes[cs.key] || cs.note}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section icon={Cog} title={c.s6.title} subtitle={c.s6.sub} testId="wls1-monetization">
        <ul className="grid gap-2 sm:grid-cols-2">
          {MONETIZATION_MODES.map((m) => (
            <li key={m.key} data-testid={`wls1-monetization-${m.key}`} className="rounded border border-slate-200 bg-white p-2 text-xs">
              <Pill tone="indigo">{m.key}</Pill>
              <p className="mt-1 text-[11px] text-slate-600">{c.monetizationNotes[m.key] || m.note}</p>
            </li>
          ))}
        </ul>
      </Section>

      <Section icon={Inbox} title={c.s7.title} subtitle={c.s7.sub} testId="wls1-offer-capability">
        <table className="w-full text-xs">
          <thead className="border-b border-slate-200 text-left text-slate-500"><tr><th className="py-1 pr-2">{c.th_capability}</th><th>{c.th_status}</th><th>{c.th_reason}</th></tr></thead>
          <tbody>
            {OFFER_CAPABILITY.map((cap) => (
              <tr key={cap.capability} data-testid={`wls1-offer-cap-${cap.capability}`} className="border-b border-slate-100">
                <td className="py-1 pr-2 font-mono text-[11px]">{cap.capability}</td>
                <td><Pill tone={cap.status === "blocked" ? "rose" : cap.status === "mock_only" ? "amber" : "slate"}>{cap.status}</Pill></td>
                <td className="py-1 text-[11px] text-slate-600">{c.offerReasons[cap.capability] || cap.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section icon={FileCheck} title={c.s8.title} subtitle={c.s8.sub} testId="wls1-audit-readiness">
        <ul className="space-y-1 text-xs">
          {c.auditRules.map((r, i) => (
            <li key={i} data-testid={`wls1-audit-rule-${i}`} className="rounded border border-slate-200 bg-slate-50/40 p-2 text-[11px]">{r}</li>
          ))}
        </ul>
      </Section>

      <footer data-testid="wls1-footer-legal" className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-3 text-[11px] italic text-slate-600">
        {c.footerLegal}
      </footer>
    </div>
  );
};

export default AdminGearStoreAccess;
